/**
 * The desktop chat's protocol, without any I/O: Claude Code's stream-json events, the
 * <vephus-action> blocks the AI writes, their validation, and the first turn. Kept apart from
 * localChat.ts (which runs actions through the API) so it can be used and tested anywhere.
 */

import type { Teammate } from './types'

export interface TokenUsage {
  input: number
  output: number
  cached: number
}

export type ChatEvent =
  | { kind: 'init'; model: string | null; sessionId: string | null }
  | { kind: 'text'; text: string }
  /** `command`: the whole command a tool ran, when it ran one — the full log level sends it (masked). */
  | { kind: 'tool'; name: string; detail: string; command?: string }
  | { kind: 'result'; text: string; sessionId: string | null; costUsd: number | null; isError: boolean; usage: TokenUsage | null }

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value) : 0
}

function usageOf(value: unknown): TokenUsage | null {
  if (!value || typeof value !== 'object') return null
  const u = value as Record<string, unknown>
  const usage = {
    input: count(u.input_tokens) + count(u.cache_creation_input_tokens),
    output: count(u.output_tokens),
    cached: count(u.cache_read_input_tokens),
  }
  return usage.input + usage.output + usage.cached > 0 ? usage : null
}

/** One line of `claude -p --output-format stream-json --verbose`, as the events the chat shows. */
export function parseStreamLine(line: string): ChatEvent[] {
  const trimmed = line.trim()
  if (!trimmed.startsWith('{')) return []
  let event: Record<string, unknown>
  try {
    event = JSON.parse(trimmed) as Record<string, unknown>
  } catch {
    return []
  }
  if (event.type === 'system' && event.subtype === 'init') {
    return [{
      kind: 'init',
      model: typeof event.model === 'string' ? event.model : null,
      sessionId: typeof event.session_id === 'string' ? event.session_id : null,
    }]
  }
  if (event.type === 'assistant') {
    const content = ((event.message as { content?: unknown[] } | undefined)?.content ?? []) as Array<Record<string, unknown>>
    return content.flatMap((part): ChatEvent[] => {
      if (part.type === 'text' && typeof part.text === 'string') return [{ kind: 'text', text: part.text }]
      if (part.type === 'tool_use') return [{ kind: 'tool', name: String(part.name ?? 'tool'), detail: toolDetail(part.input), ...wholeCommand(part.input) }]
      return []
    })
  }
  if (event.type === 'result') {
    return [{
      kind: 'result',
      text: typeof event.result === 'string' ? event.result : '',
      sessionId: typeof event.session_id === 'string' ? event.session_id : null,
      costUsd: typeof event.total_cost_usd === 'number' ? event.total_cost_usd : null,
      isError: event.is_error === true,
      usage: usageOf(event.usage),
    }]
  }
  return []
}

/** The command a tool ran, whole, when it ran one. */
function wholeCommand(input: unknown): { command?: string } {
  const command = input && typeof input === 'object' ? (input as Record<string, unknown>).command : undefined
  return typeof command === 'string' && command.trim() ? { command: command.trim() } : {}
}

/** A short line for what a tool is doing: the command, the file, the pattern. */
function toolDetail(input: unknown): string {
  if (!input || typeof input !== 'object') return ''
  const i = input as Record<string, unknown>
  const value = i.description ?? i.command ?? i.file_path ?? i.path ?? i.pattern ?? i.url ?? ''
  const text = String(value).replace(/\s+/g, ' ').trim()
  return text.length > 140 ? `${text.slice(0, 139)}…` : text
}

/**
 * One line of `opencode run --format json`, as the same events (IA local SDD, task 4). Recorded for
 * real in test/fixtures/opencode: the envelope is {type, sessionID, part}; a tool arrives twice
 * (pending, then `tool_use` with its final state) so only the final one becomes an event; each
 * step_finish carries that step's own tokens and cost (0 on ollama) — the consumer accumulates
 * across steps; an `error` event reads as a failed result. Session ids are OpenCode's own (ses_…),
 * which the page adopts for the next turn's --session.
 */
export function parseOpenCodeLine(line: string): ChatEvent[] {
  const trimmed = line.trim()
  if (!trimmed.startsWith('{')) return []
  let event: Record<string, unknown>
  try {
    event = JSON.parse(trimmed) as Record<string, unknown>
  } catch {
    return []
  }
  const sessionId = typeof event.sessionID === 'string' ? event.sessionID : null
  const part = (event.part ?? {}) as Record<string, unknown>
  if (event.type === 'step_start') {
    return [{ kind: 'init', model: null, sessionId }]
  }
  if (event.type === 'text' && typeof part.text === 'string') {
    return [{ kind: 'text', text: part.text }]
  }
  if (event.type === 'tool_use') {
    const state = (part.state ?? {}) as Record<string, unknown>
    const input = (state.input ?? {}) as Record<string, unknown>
    const subject = typeof input.filePath === 'string' ? input.filePath
      : typeof input.command === 'string' ? input.command
      : typeof state.title === 'string' ? state.title : ''
    const error = state.status === 'error' && typeof state.error === 'string' ? state.error : ''
    const detail = [subject, error].filter(Boolean).join(' — ').replace(/\s+/g, ' ').trim()
    return [{ kind: 'tool', name: String(part.tool ?? 'tool'), detail: detail.length > 140 ? `${detail.slice(0, 139)}…` : detail, ...wholeCommand(input) }]
  }
  if (event.type === 'step_finish') {
    const tokens = (part.tokens ?? {}) as Record<string, unknown>
    const cache = (tokens.cache ?? {}) as Record<string, unknown>
    const usage = { input: count(tokens.input) + count(cache.write), output: count(tokens.output), cached: count(cache.read) }
    return [{
      kind: 'result',
      text: '',
      sessionId,
      costUsd: typeof part.cost === 'number' && Number.isFinite(part.cost) ? part.cost : null,
      isError: false,
      usage: usage.input + usage.output + usage.cached > 0 ? usage : null,
    }]
  }
  if (event.type === 'error') {
    const error = (event.error ?? {}) as Record<string, unknown>
    const data = (error.data ?? {}) as Record<string, unknown>
    const message = typeof data.message === 'string' ? data.message : typeof error.name === 'string' ? error.name : 'error'
    return [{ kind: 'result', text: message, sessionId, costUsd: null, isError: true, usage: null }]
  }
  return []
}

export type ChatAction =
  | { type: 'work_order'; kind: 'Once' | 'UntilDone' | 'Scheduled'; instructions: string; intervalMinutes?: number }
  | { type: 'team'; instructions: string; builder?: string; reviewer?: string }
  | { type: 'queue'; instructions: string }
  | { type: 'doc'; path: string; content: string }
  | { type: 'pin'; path: string }
  | { type: 'audit'; key: string }
  | { type: 'open'; route: string }

const ACTION = /<vephus-action>([\s\S]*?)<\/vephus-action>/g
const ROUTES = ['/agents/work', '/live', '/docs', '/docs/pinned', '/audit', '/remote', '/agents/repositories', '/dashboard']

/** The action blocks in a reply, checked; and the reply without them. Malformed blocks are dropped. */
export function extractActions(text: string): { text: string; actions: ChatAction[] } {
  const actions: ChatAction[] = []
  for (const match of text.matchAll(ACTION)) {
    const action = checkAction(match[1] ?? '')
    if (action) actions.push(action)
  }
  return { text: text.replace(ACTION, '').replace(/\n{3,}/g, '\n\n').trim(), actions }
}

function str(value: unknown, max: number): string | null {
  return typeof value === 'string' && value.trim() && value.length <= max ? value.trim() : null
}

export function checkAction(json: string): ChatAction | null {
  let raw: Record<string, unknown>
  try {
    raw = JSON.parse(json.trim()) as Record<string, unknown>
  } catch {
    return null
  }
  switch (raw.type) {
    case 'work_order': {
      const instructions = str(raw.instructions, 10_000)
      const kind = raw.kind === 'Once' || raw.kind === 'UntilDone' || raw.kind === 'Scheduled' ? raw.kind : null
      if (!instructions || !kind) return null
      const interval = typeof raw.intervalMinutes === 'number' ? Math.round(raw.intervalMinutes) : undefined
      if (kind === 'Scheduled' && (!interval || interval < 5 || interval > 10080)) return null
      return { type: 'work_order', kind, instructions, intervalMinutes: kind === 'Scheduled' ? interval : undefined }
    }
    case 'team': {
      const instructions = str(raw.instructions, 10_000)
      return instructions ? { type: 'team', instructions, builder: str(raw.builder, 200) ?? undefined, reviewer: str(raw.reviewer, 200) ?? undefined } : null
    }
    case 'queue': {
      const instructions = str(raw.instructions, 10_000)
      return instructions ? { type: 'queue', instructions } : null
    }
    case 'doc': {
      const path = str(raw.path, 300)
      const content = typeof raw.content === 'string' && raw.content.length <= 500_000 ? raw.content : null
      return path && /\.md$/i.test(path) && content !== null ? { type: 'doc', path, content } : null
    }
    case 'pin': {
      const path = str(raw.path, 300)
      return path ? { type: 'pin', path } : null
    }
    case 'audit': {
      const key = str(raw.key, 60)
      return key && /^audit\.[a-z-]+$/.test(key) ? { type: 'audit', key } : null
    }
    case 'open': {
      const route = str(raw.route, 100)
      return route && ROUTES.includes(route) ? { type: 'open', route } : null
    }
    default:
      return null
  }
}

/** The teammate a name means: exact, then by first name, case-insensitive. */
export function findTeammate(teammates: Teammate[], name: string | undefined): Teammate | null {
  if (!name) return null
  const wanted = name.trim().toLowerCase()
  return (
    teammates.find((t) => t.name.toLowerCase() === wanted) ??
    teammates.find((t) => t.name.toLowerCase().split(/\s+/)[0] === wanted.split(/\s+/)[0]) ??
    null
  )
}

/** What the first turn carries before the person's message: the catalog's instructions, filled in. */
export function firstTurn(template: string, facts: { repository: string; branch: string | null; teammates: Teammate[] }, message: string): string {
  const intro = template
    .replace('{{repository}}', facts.repository)
    .replace('{{branch}}', facts.branch ?? 'unknown')
    .replace('{{teammates}}', facts.teammates.map((t) => t.name).join(', ') || 'none')
  return `${intro}\n\n---\n\n${message}`
}
